import { memo } from 'react'
import { Clipboard as ClipboardIcon } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import type { ChatAttachment, FinalizedAttachment } from '../../../../types'
import { getWordCountFormatted } from '../../../../utils/stringUtils'
import { FadingScrollable } from '../../../elements/FadingScrollable'
import { PreviewableContext } from '../../input/context/previewable-context'
import { AttachmentStub } from '../attachment-stub'
import { useRemoveAttachment } from '../hooks/use-remove-attachment'
import { scrollableAreaClassName, scrollablePreviewClassName } from '../styles'
import type { AttachmentPillProps } from '../types'

export const Clipboard = memo((attachment: AttachmentPillProps) => {
  const onRemove = useRemoveAttachment(attachment)

  return (
    <PreviewableContext preview={<ClipboardPreview {...attachment} />}>
      <AttachmentStub
        label={attachment.metadata?.label ?? 'Clipboard'}
        icon={<ClipboardIcon size={16} />}
        onRemove={onRemove}
      />
    </PreviewableContext>
  )
})
Clipboard.displayName = 'ClipboardText'

const ClipboardPreview = memo((attachment: ChatAttachment) => {
  const clipboard = attachment.attachment as Extract<FinalizedAttachment, { type: 'clipboardText' }>
  return (
    <div className={twMerge(scrollablePreviewClassName, 'min-h-24 min-w-24 p-4 pb-0 pt-3.5')}>
      {/* A labeled attachment is structured context, not pasted text, so a word count says nothing. */}
      {!attachment.metadata?.label && (
        <span className={'font-medium'}>{getWordCountFormatted(clipboard.text)} words</span>
      )}
      <FadingScrollable className={twMerge(scrollableAreaClassName, 'pb-2')} onClick={(e) => e.stopPropagation()}>
        {clipboard.text}
      </FadingScrollable>
    </div>
  )
})
ClipboardPreview.displayName = 'ClipboardPreview'
